/**
 * La cadenza con cui una presa multipla si percorre a schermo.
 *
 * Sta in `shared` e non piu' nel web perche' ora la leggono in due, e per ragioni
 * diverse: il client per DISEGNARE i salti, il server per non addebitare all'avversario
 * il tempo in cui li guarda (vedi `switchClock`). Se le due copie divergessero, la grazia
 * concessa dall'orologio smetterebbe di combaciare con l'animazione che la giustifica —
 * ed e' un disallineamento che nessuno vedrebbe finche' non conta i decimi.
 */

import type { Move } from '../types/board.js';

/**
 * Intervallo fra un salto e l'altro di una presa multipla.
 *
 * E' l'UNICA cadenza, e sta qui proprio per restare unica: la usano sia la presa giocata
 * a mano (`autoPlaySteps` in GameBoard) sia il replay della presa altrui (`captureReplay`
 * in game-store). Per un periodo la seconda semplicemente non esisteva — la mossa
 * dell'avversario compariva tutta in un fotogramma — e la stessa mossa aveva due
 * comportamenti opposti a seconda di chi la giocava. Cambiarne uno solo rimetterebbe li'.
 */
export const CAPTURE_STEP_MS = 300;

/**
 * Quanto dura a schermo l'animazione di `move`. Zero per una mossa semplice.
 *
 * `path` sono le caselle d'arrivo (escluso `from`, incluso `to`) e il motore lo valorizza
 * SOLO da due salti in su (`capture-moves.ts`: `pathSoFar.length > 1 ? pathSoFar : undefined`),
 * quindi una presa di un salto solo e una mossa semplice cadono entrambe sullo zero.
 * Il primo salto e' mostrato SUBITO, senza attesa: da li' il `- 1`.
 *
 * E' la stessa durata di tre cose che devono coincidere, o il conto dell'orologio non
 * torna: il ritardo con cui chi prende spedisce la mossa, la durata del fantasma che
 * l'avversario si guarda, e la grazia che il server accredita a quest'ultimo.
 */
export function durataAnimazionePresa(move: Pick<Move, 'path'>): number {
  const salti = move.path?.length ?? 0;
  return salti > 1 ? (salti - 1) * CAPTURE_STEP_MS : 0;
}
